import React, { useEffect, useState } from 'react';
import { Container, Button, Box, Typography, Stepper, Step, StepLabel, Paper } from '@mui/material';
import useHoroscopeApi from '../../api/useHoroscopeApi';
import useUserApi from '../../api/useUserApi';
import { UserBase, LoginRequest } from '../../types/user';
import { UserDetailsStep, HoroscopeTypeStep, ReviewStep, HoroscopeResult, HoroscopeState } from '.';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import useContentApi from '../../api/useContentApi';
import { ContentType, Content } from '../../types/content';
import { getHoroscopeTypeText } from '../../services/zodiacService';
import Logo from '../../components/Logo';
import { formatBirthDateTime } from '../../services/time.service';


const steps = ['פרטי משתמש', 'בחר סוג הורוסקופ', 'בדוק ושלח'];

const Horoscope: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [userData, setUserData] = useState<UserBase>();
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [interestAreas, setInterestAreas] = useState<Content[]>([]);
  const api = useHoroscopeApi();
  const { register, updateProfile, login } = useUserApi();
  const [state, setState] = useState<HoroscopeState>({
    horoscopeType: 'daily',
    isFormValid: false,
    selectedInterests: [],
  });
  const { getContentByType } = useContentApi();
  const { user, reloadUser } = useAuth();

  useEffect(() => {
    if (user) {
      setUserData(user);
      setActiveStep(1);
    } else {
      setActiveStep(0);
      setUserData(undefined);
    }
  }, [user]);

  useEffect(() => {
    const fetchInterestAreas = async () => {
      try {
        setLoading(true);
        const data = await getContentByType(ContentType.INTEREST_AREA);
        setInterestAreas(data);
        // Set all interests as selected by default
        const allInterests = data.map((area) => area.title || '');
        setState({ ...state, selectedInterests: allInterests });
        setError('');
      } catch (err) {
        console.error('Failed to fetch interest areas:', err);
        setError('Failed to load interest areas');
      } finally {
        setLoading(false);
      }
    };

    fetchInterestAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getContentByType]);

  const handleNext = async () => {
    // Only allow advancing if the form is valid
    if ((activeStep === 0 || activeStep === 1) && !isFormValid) {
      return;
    }

    // If we're on the first step, submit the user details
    if (activeStep === 0) {
      setLoading(true);
      setError('');
      try {
        if (!userData) {
          throw new Error('User data is undefined');
        }
        // Format the date to YYYY-MM-DD if it's a dayjs object
        const formattedDate = userData.birth_date && dayjs(userData.birth_date).isValid()
          ? dayjs(userData.birth_date).format('YYYY-MM-DD')
          : userData?.birth_date;
        userData.birth_date = formattedDate;

        // Handle birth_time using the formatting function
        const birth_timeObj = formatBirthDateTime(userData.birth_time);
        userData.birth_time = birth_timeObj?.format('YYYY-MM-DD HH:mm:ss');

        if (userData.id) {
          const updatedUser = await updateProfile(userData);
          if (updatedUser) {
            setUserData(updatedUser);
            reloadUser();
          }
        } else {
          const newUser = await register(userData);
          if (newUser) {
            const res = await login({ username: userData.email || '', password: userData.password || '' } as LoginRequest);
            localStorage.setItem('token', res.access_token);
            setUserData(newUser);
            reloadUser();
          }
        }
        // Advance to the next step
        setActiveStep((prev) => prev + 1);

      } catch (err: any) {
        console.error('Error submitting user details:', err);
        // Don't show error to user, just log it and continue
      } finally {
        setLoading(false);
      }
    } else if (activeStep === 1) {
      if (state.selectedInterests.length > 0) {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(1);
    setResult('');
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setResult('');
    try {
      if (!userData) {
        throw new Error('User data is undefined');
      }
      if (userData.id && userData.birth_time && userData.birth_date) {
        const response = await api.generateHoroscopePersonalized({
          id: userData.id,
          horoscope_type: state.horoscopeType,
          interests: state.selectedInterests,
        });
        setResult(response.horoscope);
      } else if (userData.id) {
        const response = await api.generateHoroscope({
          id: userData.id,
          horoscope_type: state.horoscopeType,
          interests: state.selectedInterests,
        });
        setResult(response.horoscope);
      } else {
        throw new Error('User ID is undefined');
      }

    } catch (err: any) {
      setError('אירעה שגיאה בעת יצירת ההורוסקופ. אנא נסה שוב.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <UserDetailsStep
            userData={userData}
            setUserData={setUserData}
            onValidationChange={setIsFormValid}
          />
        );
      case 1:
        return (
          <HoroscopeTypeStep
            parentHoroscopeType={state.horoscopeType}
            parentSelectedInterests={state.selectedInterests}
            interestsAreas={interestAreas.map((area) => ({ name: area.content, title: area.title || '' }))}
            onValidationChange={setIsFormValid}
            updateHoroscopeType={(type) => setState({ ...state, horoscopeType: type })}
            updateSelectedInterests={(interests) => setState({ ...state, selectedInterests: interests })}
          />
        );
      case 2:
        return (
          userData && <ReviewStep state={state} userData={userData} />
        );
      default:
        return 'שלב לא מוגדר';
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: 4,
        px: { xs: 2, sm: 3, md: 4 },
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '900px', lg: '1200px' },
        mx: 'auto'
      }}>
      <Logo />

      <Stepper activeStep={activeStep} alternativeLabel dir="rtl" sx={{
        justifyContent: 'space-around',
        '& .MuiStepConnector-line': {
          borderColor: 'rgba(0, 0, 0, 0.23)'
        },
        '& .MuiStepConnector-alternativeLabel': {
          right: 'calc(-50% + 20px)',
          left: 'calc(50% + 20px)'
        },
        '& .MuiStepLabel-root': { flexDirection: 'column' },
        '& .MuiStepLabel-labelContainer': { marginTop: 1 },
        '& .Mui-active': { color: 'primary.main' },
        '& .Mui-completed': { color: 'success.main' }
      }}>
        {steps.map((label, index) => {
          const isActive = activeStep === index;
          const isCompleted = activeStep > index;
          return (
            <Step key={index} completed={isCompleted}>
              <StepLabel
                StepIconProps={{
                  active: isActive,
                  completed: isCompleted
                }}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box sx={{ mt: 4, mb: 2 }}>
        <Paper elevation={0} sx={{
          background: 'linear-gradient(135deg, rgba(40,53,74,0.95) 60%, rgba(50,60,90,0.98) 100%)',
          border: '1px solid rgba(126, 161, 255, 0.18)', // subtle blue border
          borderRadius: '14px',
          boxShadow: '0 2px 16px 0 rgba(40,53,74,0.10)',
          p: 4,
          mb: 2,
        }}>
          {renderStepContent(activeStep)}
        </Paper>
      </Box>
      <Box display="flex" justifyContent="space-between">
        {activeStep > 0 && !result && (
          <Button variant="contained" onClick={handleBack}>
            הקודם
          </Button>
        )}
        {result ? <HoroscopeResult result={result} type={getHoroscopeTypeText(state.horoscopeType)} onReset={handleReset} /> : (
          <>
            {activeStep < steps.length - 1 && (
              <Button variant="contained" onClick={handleNext} disabled={(activeStep === 0 || activeStep === 1) && (!isFormValid || loading)}>
                {activeStep === 0 && loading ? 'שולח פרטים...' : 'הבא'}
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                {loading ? 'יוצר הורוסקופ...' : 'סיום'}
              </Button>
            )}
          </>
        )}
      </Box>
      {error && (
        <Typography color="error" align="center" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Container>
  );
};

export default Horoscope;
